<template>
  <div v-if="useCustomScores">
    <CustomSelect
      :invalid="scoreInvalid"
      errorMsg="Please select an assessment provider from the list"
      v-model="score"
      :options="customScores"
      :label="scoreLabel"
      @change="updateScore"
    >
    </CustomSelect>
  </div>
  <div v-else>
    <q-field :error="scoreInvalid" :error-label="scoreErrorLabel" :helper="scoreHint">
      <!-- https://github.com/quasarframework/quasar/issues/1906#issuecomment-386029700 -->
      <q-input
        autocomplete="off"
        maxlength="5"
        :dark="dark"
        color="secondary"

        v-model="score"
        @blur="emitChange"
        @keyup.enter="emitChange"
        :float-label="scoreLabel"
		type="number"
		:min="minAllowed"
		:max="maxAllowed"
		:step="1"
      />
    </q-field>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Emit } from 'vue-property-decorator';
import { mapMutations } from 'vuex';
import { AssessmentEntry } from '@/models';
import config from '@/models/services/config';
import assessmentEntryValidator from '@/models/validation/AssessmentEntryValidator';
import { IScorePolicy } from '@/models/services/collegetransfer/entities';
import CustomSelect from '@/components/CustomSelect.vue';

@Component({
	methods: mapMutations('wmct', {
		updateAssessmentScore: 'UPDATE_ASSESSMENT_SCORE',
	}),
	components: {
		CustomSelect,
	},
})
export default class GradeInput extends Vue {
	@Prop() public value!: AssessmentEntry;

	public get scoreLabel(): string {
		return 'Score';
	}

	public get dark() {
		return config.Dark;
	}

	private updateAssessmentScore!: ({
		assessment,
		score,
	}: {
		assessment: AssessmentEntry;
		score: string | null;
	}) => void;

	private get validating() {
		return this.value.isValidating;
	}

	public get score() {
		return this.value.score;
	}
	public set score(score: string | null) {
		const scoreToUpdate = (score === '') ? null : score;
		this.updateAssessmentScore({ assessment: this.value, score: scoreToUpdate });
	}

	public get scoreInvalid() {
		return this.validating && !assessmentEntryValidator.isScoreValid(this.score, this.scorePolicy);
	}

	public get scorePolicy(): IScorePolicy | null {
		return  this.value.institution.scorePolicy;
	}

	public get useCustomScores(): boolean {
		const sp = this.scorePolicy;
		return sp !== null && sp.UseCustomScores;
	}

	public get customScores(): any[] {
		const sp = this.scorePolicy;
		if (sp === null) {
			return [];
		}

		const options = sp.CustomScores.slice().sort((a, b) => a.Rank - b.Rank)
				.map((v) => ({ label: v.Display, value: v.Display}));

		options.unshift({ label: '', value: '' })
		return options;
	}

	public get minAllowed(): number | null {
		const sp = this.scorePolicy;
		if (sp === null) {
			return null;
		}
		return sp.LowScore;
	}

	public get maxAllowed(): number | null {
		const sp = this.scorePolicy;
		if (sp === null) {
			return null;
		}
		return sp.HighScore;
	}

	public get scoreErrorLabel(): string {
		const min = this.minAllowed;
		const max = this.maxAllowed;
		return (min === null && max === null) ? ''
				: `Please enter a ${this.scoreLabel} between ${min} and ${max}`;
	}

	public get scoreHint(): string {
		const min = this.minAllowed;
		const max = this.maxAllowed;
		return (min === null && max === null) ? ''
				: `Enter a ${this.scoreLabel} between ${min} and ${max}`;
	}

	public updateScore(val: string | null) {
		this.score = val;
	}

	@Emit('change')
	public emitChange(newValue: string) {
		/* */
	}
}
</script>
